import { useEffect, useState } from 'react';

import { useMapGetBboxQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/map/get-bbox/query';
import { mapPreparedMapBboxSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/map/get-bbox/selectors';
import { useGetYandexMapScriptQuery } from '@/packages/query-state/shared/store/yandex-map/2.1/get-yandex-map/query';
import { useFiltersBboxSelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/dumb';
import { useListingLayoutViewModeSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';
import { useMapConfigurationInitialStateSelector } from '@/packages/redux-state/store/slices/mapConfiguration';

import { Y_MAP_DEFAULT_MARGIN, Y_MAP_DEFAULT_STATE } from '../constants/map';
import { convertBboxToMapBounds } from '../utils/map/convertBboxToMapBounds';

import { useMapSize } from './useMapSize';

interface IUseMapInitialStateParams {
  mapRef: React.RefObject<HTMLDivElement>;
  ymaps: YMaps.IYMaps | null;
}

export const useMapInitialState = (params: IUseMapInitialStateParams): YMaps.IMapState | undefined => {
  const { ymaps } = params;

  const [mapState, setMapState] = useState<YMaps.IMapState>();

  const bbox = useFiltersBboxSelector();

  const viewMode = useListingLayoutViewModeSelector();

  const { center, zoom } = useMapConfigurationInitialStateSelector();

  const { isSuccess: isLoadMapScriptSuccess } = useGetYandexMapScriptQuery();

  const isMapInitialBboxQueryEnabled = !center && !zoom && !bbox && isLoadMapScriptSuccess;

  const { data: initialMapBbox, isSuccess } = useMapGetBboxQuery({
    enabled: isMapInitialBboxQueryEnabled,
    select: mapPreparedMapBboxSelector,
  });

  const mapSize = useMapSize(params);

  useEffect(() => {
    if (!ymaps || !mapSize || mapState || viewMode === 'list') {
      return;
    }

    const { width, height } = mapSize;

    let state;

    if (center && zoom) {
      /** center и zoom пришли из queryString */
      state = { center, zoom };
    } else if (bbox) {
      /** bbox пришел из queryString */
      state = ymaps.util.bounds.getCenterAndZoom(convertBboxToMapBounds(bbox), [width, height]);
    } else if (isMapInitialBboxQueryEnabled && isSuccess) {
      /** bbox получили от бэка, если в квери нет параметров для центрирования */
      state = ymaps.util.bounds.getCenterAndZoom(initialMapBbox, [width, height]);
    } else if (!isMapInitialBboxQueryEnabled && !isSuccess) {
      /** стейт по умолчанию, если бэк не отработал */
      state = Y_MAP_DEFAULT_STATE;
    }

    if (state) {
      setMapState({ ...state, margin: Y_MAP_DEFAULT_MARGIN });
    }
  }, [initialMapBbox, ymaps, mapState, viewMode, center, zoom, isSuccess, isMapInitialBboxQueryEnabled, bbox, mapSize]);

  return mapState;
};
